// src/components/DeleteAccountModal.jsx
// Full account deletion modal — cancels Stripe, deletes all data, removes auth user.

import { useState } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL ?? '';
const ANON_KEY     = process.env.REACT_APP_SUPABASE_ANON_KEY ?? '';

// Folded into billing-portal to stay within Vercel Hobby's 12-function limit.
const DELETE_ENDPOINT = '/api/billing-portal';

const PROJECT_REF = SUPABASE_URL.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] ?? '';
const AUTH_KEY    = `sb-${PROJECT_REF}-auth-token`;

function getStoredSession() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// Directly call Supabase token refresh endpoint — bypasses the JS client which hangs.
async function refreshToken(refreshTkn) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 10000);
  try {
    const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'apikey': ANON_KEY },
      body: JSON.stringify({ refresh_token: refreshTkn }),
      signal: ctrl.signal,
    });
    if (!res.ok) return null;
    const newSession = await res.json();
    // Persist new session so the app picks it up after the page reloads
    localStorage.setItem(AUTH_KEY, JSON.stringify(newSession));
    return newSession.access_token ?? null;
  } catch { return null; }
  finally { clearTimeout(t); }
}

// All user-owned localStorage keys — cleared on deletion so a new account on the
// same browser starts clean. cookie_notice_accepted is intentionally kept.
const USER_LS_KEYS = [
  'onboarding_complete',
  'terms_pending_acceptance',
  'wm_active_layers',
  'portfolio_os_settings',
  'portfolio_os_windows',
  // legacy keys from pre-Supabase era
  'holdings',
  'riskTolerance',
  'mw_watchlist',
  'portfolio_os_user_id',
];

function clearAllUserStorage() {
  try {
    // Auth tokens
    localStorage.removeItem(AUTH_KEY);
    Object.keys(localStorage)
      .filter(k => k.startsWith(`sb-${PROJECT_REF}-`))
      .forEach(k => localStorage.removeItem(k));
    // App state
    USER_LS_KEYS.forEach(k => localStorage.removeItem(k));
  } catch {}
}

export default function DeleteAccountModal({ onClose }) {
  const [confirmText, setConfirmText] = useState('');
  const [step,        setStep]        = useState('confirm'); // 'confirm' | 'deleting' | 'done'
  const [error,       setError]       = useState(null);

  const isConfirmed = confirmText === 'DELETE';

  const handleDelete = async () => {
    if (!isConfirmed) return;
    setStep('deleting');
    setError(null);

    try {
      // Get a valid access token without using the Supabase JS client (which hangs).
      // 1. Read from localStorage. 2. If expired, refresh directly via REST API.
      const stored = getStoredSession();
      if (!stored?.access_token) {
        clearAllUserStorage();
        window.location.href = '/';
        return;
      }

      // Check if the token looks expired (exp is in seconds, Date.now() in ms)
      let accessToken = stored.access_token;
      const exp = stored.expires_at ?? (stored.exp ?? 0);
      const isExpired = exp > 0 && exp < Math.floor(Date.now() / 1000) + 30;

      if (isExpired && stored.refresh_token) {
        const refreshed = await refreshToken(stored.refresh_token);
        if (!refreshed) {
          setError('Could not refresh your session. Please sign out and sign back in, then try again.');
          setStep('confirm');
          return;
        }
        accessToken = refreshed;
      }

      const controller = new AbortController();
      const fetchTimeout = setTimeout(() => controller.abort(), 30000);

      // Call the Vercel API route — same origin, no CORS complexity.
      const apiHeaders = { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' };

      const deleteBody = JSON.stringify({ action: 'delete_account' });

      let res;
      try {
        res = await fetch(DELETE_ENDPOINT, {
          method: 'POST',
          headers: apiHeaders,
          body: deleteBody,
          signal: controller.signal,
        });
      } finally {
        clearTimeout(fetchTimeout);
      }

      const data = await res.json().catch(() => ({}));

      if (res.status === 401) {
        // JWT rejected — refresh once and retry.
        if (stored?.refresh_token) {
          const retried = await refreshToken(stored.refresh_token);
          if (retried) {
            const res2 = await fetch(DELETE_ENDPOINT, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${retried}`, 'Content-Type': 'application/json' },
              body: deleteBody,
            });
            const data2 = await res2.json().catch(() => ({}));
            if (res2.ok && data2.success) {
              setStep('done');
              clearAllUserStorage();
              setTimeout(() => { window.location.href = '/?deleted=true'; }, 2000);
              return;
            }
            setError(data2.error ?? `Deletion failed (${res2.status}). Please contact support@nuvportos.com`);
            setStep('confirm');
            return;
          }
        }
        setError('Session expired. Please sign out, sign back in, and try again.');
        setStep('confirm');
        return;
      }

      if (!res.ok || !data.success) {
        setError(data.error ?? 'Deletion failed. Please contact support@nuvportos.com');
        setStep('confirm');
        return;
      }

      setStep('done');
      clearAllUserStorage();
      setTimeout(() => { window.location.href = '/?deleted=true'; }, 2000);

    } catch (err) {
      console.error('[DeleteAccountModal]', err);
      setError('An unexpected error occurred. Please contact support@nuvportos.com');
      setStep('confirm');
    }
  };

  // ── Done state ────────────────────────────────────────────
  if (step === 'done') {
    return (
      <div style={S.overlay}>
        <div style={S.modal}>
          <div style={{ textAlign: 'center', padding: '48px 32px' }}>
            <div style={{ fontSize: 32, color: '#3ddc84', marginBottom: 16 }}>✓</div>
            <div style={{ fontFamily: 'IBM Plex Mono', fontSize: 13, color: '#3ddc84', marginBottom: 8 }}>
              Account deleted successfully.
            </div>
            <div style={{ fontFamily: 'IBM Plex Mono', fontSize: 10, color: '#8b949e' }}>
              Redirecting to homepage…
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Deleting state ────────────────────────────────────────
  if (step === 'deleting') {
    return (
      <div style={S.overlay}>
        <div style={S.modal}>
          <div style={{ textAlign: 'center', padding: '48px 32px' }}>
            <div style={{
              width: 28, height: 28,
              border: '3px solid #1a2a1f',
              borderTop: '3px solid #3ddc84',
              borderRadius: '50%',
              animation: 'da-spin 0.8s linear infinite',
              margin: '0 auto 20px',
            }} />
            <style>{`@keyframes da-spin { to { transform: rotate(360deg); } }`}</style>
            <div style={{ fontFamily: 'IBM Plex Mono', fontSize: 12, color: '#8b949e', marginBottom: 6 }}>
              Deleting your account…
            </div>
            <div style={{ fontFamily: 'IBM Plex Mono', fontSize: 10, color: '#4b5563', letterSpacing: '.04em' }}>
              Cancelling subscription · Removing data · Deleting auth record
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Confirm state ─────────────────────────────────────────
  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>

        {/* Header */}
        <div style={S.header}>
          <div style={{ fontFamily: 'IBM Plex Mono', fontSize: 10, color: '#ff6b35', letterSpacing: '.14em' }}>
            ⚠ DELETE ACCOUNT
          </div>
          <button onClick={onClose} style={S.closeBtn}>✕</button>
        </div>

        {/* Warning */}
        <div style={S.warning}>
          <div style={{ color: '#c9d1d9', fontSize: 13, lineHeight: 1.7, marginBottom: 12 }}>
            This action is <strong style={{ color: '#ff6b35' }}>permanent and cannot be undone</strong>.
          </div>
          <div style={{ color: '#8b949e', fontSize: 12, lineHeight: 1.8 }}>
            The following will be permanently deleted:
          </div>
          <ul style={{ color: '#8b949e', fontSize: 12, lineHeight: 2, paddingLeft: 18, marginTop: 4, marginBottom: 0 }}>
            <li>All portfolio holdings and snapshots</li>
            <li>Your watchlist</li>
            <li>All account settings and preferences</li>
            <li>Your active subscription (cancelled immediately)</li>
            <li>Your login credentials</li>
          </ul>
        </div>

        {/* Confirm input */}
        <div style={{ padding: '0 24px 12px' }}>
          <div style={{ fontFamily: 'IBM Plex Mono', fontSize: 10, color: '#8b949e', marginBottom: 8 }}>
            Type <span style={{ color: '#ff6b35', fontWeight: 700 }}>DELETE</span> to confirm
          </div>
          <input
            type="text"
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && isConfirmed && handleDelete()}
            placeholder="Type DELETE here"
            autoFocus
            style={S.input}
          />
        </div>

        {/* Error */}
        {error && (
          <div style={{ padding: '0 24px 8px', fontFamily: 'IBM Plex Mono', fontSize: 11, color: '#ff6b35', lineHeight: 1.6 }}>
            {error}
          </div>
        )}

        {/* Actions */}
        <div style={S.actions}>
          <button onClick={onClose} style={S.cancelBtn}>Cancel</button>
          <button
            onClick={handleDelete}
            disabled={!isConfirmed}
            style={{ ...S.deleteBtn, opacity: isConfirmed ? 1 : 0.35, cursor: isConfirmed ? 'pointer' : 'not-allowed' }}
          >
            Delete My Account
          </button>
        </div>

      </div>
    </div>
  );
}

const S = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.85)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999,
  },
  modal: {
    background: '#0d1520',
    border: '1px solid #1c2333',
    borderRadius: 12,
    width: '100%', maxWidth: 460,
    overflow: 'hidden',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '20px 24px 16px',
    borderBottom: '1px solid #1c2333',
  },
  closeBtn: {
    background: 'none', border: 'none',
    color: '#8b949e', cursor: 'pointer', fontSize: 16, padding: 4,
  },
  warning: {
    padding: '20px 24px',
    background: 'rgba(255,107,53,0.05)',
    borderBottom: '1px solid #1c2333',
    marginBottom: 16,
  },
  input: {
    width: '100%', boxSizing: 'border-box',
    background: '#0a0a0a',
    border: '1px solid #1c2333', borderRadius: 6,
    padding: '10px 14px',
    color: '#c9d1d9',
    fontFamily: 'IBM Plex Mono', fontSize: 13,
    outline: 'none',
  },
  actions: {
    display: 'flex', gap: 12, justifyContent: 'flex-end',
    padding: '16px 24px 24px',
  },
  cancelBtn: {
    background: 'transparent', border: '1px solid #1c2333', borderRadius: 6,
    color: '#8b949e', padding: '10px 20px', cursor: 'pointer',
    fontFamily: 'IBM Plex Mono', fontSize: 12,
  },
  deleteBtn: {
    background: '#ff6b35', border: 'none', borderRadius: 6,
    color: '#fff', padding: '10px 20px',
    fontFamily: 'IBM Plex Mono', fontSize: 12, fontWeight: 700,
    transition: 'opacity 0.15s',
  },
};
